import { useEffect, useSyncExternalStore } from 'react';
import { useDocViewer, type OpenDocViewer } from './doc-viewer-presenter';
import { useChatOverlayOpen } from './use-chat-overlay';
import { chatFilePaneEnabled, useChatFilePaneEnabled } from './chat-file-pane-flag';
import { useChatsLayout } from './use-chats-layout';
import { useNarrow } from './use-narrow';

// How many chats are mounted that can draw a file pane. FloatingChat lives inside AppFrame only, so a
// chrome-free route can report `chatMode: 'expanded'` with no chat on screen at all — and a request
// docked to a chat that is not there would be drawn nowhere. Counting the mounted host closes that.
let hosts = 0;
const listeners = new Set<() => void>();
const emit = () => {
  for (const listener of [...listeners]) listener();
};
const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

/** Called by the chat that renders the pane (FloatingChat), for as long as it is mounted. */
export function useRegisterChatFilePaneHost(): void {
  useEffect(() => {
    hosts += 1;
    emit();
    return () => {
      hosts -= 1;
      emit();
    };
  }, []);
}

/**
 * The open doc-viewer request when it is docked BESIDE the chat, else `null`.
 *
 * A chat opener marks its request `besideChat`. Whether it really docks is decided here, ONCE, because
 * three places must agree on it: `FloatingChat` (which splits into file pane + conversation), `DocViewerHost`
 * (which must then draw no modal) and `AppFrame` (whose `--chat-w` must stop reserving a dock column, since
 * the split chat covers the content area the way fullscreen does). Three separate reads of "open and wide"
 * would be three chances to paint the file twice or not at all.
 *
 * The narrow test is FloatingChat's own (`useNarrow() || stacked`): wherever the chat is a full-screen sheet
 * there is no room for two columns, and the request falls back to the modal.
 *
 * Behind the Conductor experiment `symphonyChatFilePaneWeb` (`lib/chat-file-pane-flag.ts`): closed,
 * every request opens the modal exactly as before.
 */
export function useChatFilePane(): OpenDocViewer | null {
  const request = useDocViewer();
  const chatOpen = useChatOverlayOpen();
  const narrow = useNarrow();
  const { stacked } = useChatsLayout();
  const hosted = useSyncExternalStore(subscribe, () => hosts > 0, () => false);
  const enabled = useChatFilePaneEnabled();
  if (!enabled || !request?.besideChat || !hosted || !chatOpen) return null;
  // While the editor is open the presentation is FROZEN as it was when editing began: moving between pane and
  // modal remounts the viewer, and the editor's unsaved text lives in the mounted editor. A resize mid-edit
  // therefore keeps the pane (or the modal) until the editor closes, and only then follows the layout. The
  // live-refreshed copy survives a move regardless — it rides the request (`updateOpenDocViewer`).
  if (request.editing && frozen?.id === request.id) return frozen.docked ? request : null;
  const docked = !narrow && !stacked;
  frozen = { id: request.id, docked };
  return docked ? request : null;
}

/**
 * Runs `dock` — hand a file to the chat Discuss is about to open — only where it would dock rather than fall
 * back to the modal: the same experiment and the same narrow test `useChatFilePane` applies, minus the chat
 * being open. Where the answer is no, the chat is a full-screen sheet and a modal over it would hide the
 * conversation Discuss just started.
 *
 * It AWAITS the experiment rather than reading the hook's value: that value is `false` until the gate's first
 * read resolves, and a Discuss pressed in that window would otherwise drop the file for good.
 */
export function useDockBesideChat(): (dock: () => void) => void {
  const narrow = useNarrow();
  const { stacked } = useChatsLayout();
  const fits = !narrow && !stacked;
  return (dock) => {
    if (!fits) return;
    void chatFilePaneEnabled().then((on) => {
      if (on) dock();
    });
  };
}

/**
 * True while a `besideChat` request is WAITING for a chat that can host it — the experiment is on, the layout
 * is wide and a chat is mounted but not open — in one of the two moments that wait is known to end:
 *
 *   - a Discuss HANDOFF (`request.handoff`): the chat it navigates to is about to open;
 *   - the chat CLOSING on it: the last presentation decided for this request was the pane, and the chat
 *     dismisses it right after (`FloatingChat`).
 *
 * `DocViewerHost` draws nothing then, rather than the modal, which would only flash on the file's way in or
 * out. Any other closed-chat `besideChat` request — a chat link that resolved after the chat was minimized —
 * has no chat coming for it, so it is not waiting and opens as the modal.
 */
export function useBesideChatWaiting(): boolean {
  const request = useDocViewer();
  const chatOpen = useChatOverlayOpen();
  const narrow = useNarrow();
  const { stacked } = useChatsLayout();
  const hosted = useSyncExternalStore(subscribe, () => hosts > 0, () => false);
  const enabled = useChatFilePaneEnabled();
  if (!enabled || !request?.besideChat || !hosted || chatOpen || narrow || stacked) return false;
  return request.handoff === true || (frozen?.id === request.id && frozen.docked);
}

/** The last presentation decided for the open request — what an open editor holds on to. Every reader of
 *  `useChatFilePane` computes the same answer from the same inputs, so whichever records it last agrees. */
let frozen: { id: number; docked: boolean } | null = null;
